import { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { Notifications } from '@/features/notification/components/Notifications';
import { COLORS } from '@/shared/constants/colors';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { useProfileStore } from '@/shared/store/profile.store';
import { useSensitiveValuesVisibilityStore } from '@/shared/store/sensitive-values-visibility.store';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { Tooltip } from '@/shared/uikitV2/antd/Tooltip';
import { Button } from '@/shared/uikitV2/Button';

import { Breadcrumb, BreadcrumbItem } from '../../breadcrumb';
import { media } from '../media';

type PageHeaderProps = PropsWithChildren & {
  title: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  hideMenuButtons?: boolean;
};

export const PageHeader: BFC<PageHeaderProps> = ({
  title,
  breadcrumbs,
  hideMenuButtons,
  children,
  style,
  className,
}) => {
  const { t } = useTranslation('shared/components', { keyPrefix: 'pageHeader' });
  const { toggleVisibility, isVisible } = useSensitiveValuesVisibilityStore();
  const { isBeneficiary } = useProfileStore();
  const { sm, md } = useBreakpoints();

  return (
    <Container style={style} className={cn(className)}>
      <Left>
        {breadcrumbs?.length && <Breadcrumb items={breadcrumbs} />}
        <Title size={28} bold>
          {title}
        </Title>
      </Left>
      <Right>
        {!hideMenuButtons && !(sm || md) && (
          <MenuButtons>
            <Tooltip
              placement="bottom"
              title={
                isVisible ? t('sensitiveIconTooltip.visible') : t('sensitiveIconTooltip.hidden')
              }
            >
              <StyledButton
                type="default"
                size="middle"
                onClick={toggleVisibility}
                icon={<Icon name={isVisible ? 'eye' : 'eye-slash'} size="s20" />}
              />
            </Tooltip>
            {!isBeneficiary && !clientConfig.isExported && (
              <Notifications>
                {({ toggleShow }) => (
                  <StyledButton
                    type="default"
                    size="middle"
                    onClick={toggleShow}
                    icon={<Icon size="s20" name="bell" />}
                  />
                )}
              </Notifications>
            )}
          </MenuButtons>
        )}
        {children}
      </Right>
    </Container>
  );
};

const Container = styled.div`
  --page-title: minmax(95px, 100%);

  display: grid;
  align-items: start;
  grid-template-columns: var(--page-title) minmax(223px, max-content);

  padding-block: 24px;
  gap: 8px;

  [type='button'] {
    font-size: 14px;
  }

  ${media.lg} {
    grid-template-columns: var(--page-title) 224px;
  }

  ${media.sm} {
    grid-template-columns: var(--page-title) 1fr;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: end;

  ${media.lg} {
    flex-wrap: wrap;
  }
`;

const MenuButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  ${media.sm} {
    gap: 0;
  }
`;

const StyledButton = styled(Button)`
  &&& {
    background-color: ${COLORS.systemLight100};
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: ${COLORS.systemDark300};
    border: none;
  }
`;
